import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { RocketLaunch as RocketIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';

import { useSiteBatchPolling, siteBatchQueryKey } from '../../../hooks/useSiteBatchPolling';
import { BatchStep, SiteBatch, isBatchRunning, mergeStepsWithCatalog } from '../../../models/SiteBatch';
import siteBatchService from '../../../services/SiteBatchService';
import SiteBatchRow from '../widgets/BatchStep';

interface SiteBatchProgressModalProps {
  open: boolean;
  projectId: string | null;
  batchId: number | null;
  /** Optional initial snapshot from the StartBatch response — avoids an extra network round-trip on open. */
  initialBatch?: SiteBatch | null;
  siteName?: string | null;
  onClose: () => void;
}

/**
 * Modal that shows the live progress of a site-batch deployment.
 *
 * The modal is the primary surface for the 8-step sequential deploy UX:
 *  - Header: batch id + site + overall status
 *  - Summary bar: success/failed counts + linear progress (current_step / total)
 *  - Rows: one per service (waiting → dispatching → running → success/failed/timeout)
 *  - Retry: only enabled after the batch is `completed` AND the step failed
 *
 * The user can close the modal while the batch is still running — the
 * batch continues on the backend, and the outer view shows a "진행 중"
 * badge so the modal can be reopened (module-9).
 */
const SiteBatchProgressModal: React.FC<SiteBatchProgressModalProps> = ({
  open,
  projectId,
  batchId,
  initialBatch,
  siteName,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useSiteBatchPolling(projectId, batchId, {
    enabled: open && !!projectId && !!batchId,
  });

  const batch: SiteBatch | null = data ?? initialBatch ?? null;
  const [retryingIndex, setRetryingIndex] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  const canRetryStep = (step: BatchStep): boolean =>
    !!batch && batch.status === 'completed' && (step.status === 'failed' || step.status === 'timeout');

  const handleRetry = async (stepIndex: number) => {
    if (!projectId || !batchId) return;
    setRetryingIndex(stepIndex);
    setRetryError(null);
    try {
      await siteBatchService.retryStep(projectId, batchId, { step_index: stepIndex });
      // Force an immediate refetch so the UI reflects the new `dispatching` step right away.
      await queryClient.invalidateQueries({ queryKey: siteBatchQueryKey(projectId, batchId) });
    } catch (e) {
      console.error('재배포 실패:', e);
      setRetryError('재배포에 실패했습니다. 배치 상태를 확인한 뒤 다시 시도해주세요.');
    } finally {
      setRetryingIndex(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="site-batch-progress-title"
    >
      <DialogTitle id="site-batch-progress-title">
        <Stack direction="row" alignItems="center" spacing={1}>
          <RocketIcon color="primary" />
          <span>배포 진행</span>
          {batch && (
            <>
              <Chip label={`#${batch.batch_id}`} size="small" variant="outlined" />
              <Chip label={batch.site} size="small" color="primary" variant="outlined" />
              <StatusChip status={batch.status} />
            </>
          )}
          {!batch && siteName && <Chip label={siteName} size="small" variant="outlined" />}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && !batch && (
          <Stack alignItems="center" py={6}>
            <CircularProgress />
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            배포 상태를 불러오지 못했습니다: {error.message}
          </Alert>
        )}
        {retryError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRetryError(null)}>
            {retryError}
          </Alert>
        )}
        {batch && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              8개 서비스를 지정된 순서로 순차 배포합니다. 각 단계는 GitHub Actions 실행
              완료 시점에 <strong>성공 / 실패 / 시간 초과</strong> 로 전환됩니다. 일부
              단계가 실패해도 <strong>남은 단계는 계속 배포됩니다</strong> (continue-on-failure).
              완료 후 실패한 단계는 행 오른쪽 <strong>재배포</strong> 버튼으로 개별 재실행
              가능합니다.
            </Alert>
            <SummaryBar batch={batch} />
            <Stack spacing={1} sx={{ mt: 2 }}>
              {/* 8-서비스 카탈로그와 실제 DB step 을 merge 해서 항상 8행을 보여준다.
                  backend 는 dispatch 시점에서야 각 step 행을 만드는데, 그 전까지
                  UI 가 빈 상태로 보이는 걸 막기 위한 프레젠테이션 계층 결합. */}
              {mergeStepsWithCatalog(batch.steps).map((step) => (
                <SiteBatchRow
                  key={`${step.index}-${step.deployment_id ?? 'waiting'}`}
                  step={step}
                  projectId={projectId!}
                  batchId={batchId!}
                  canRetry={canRetryStep(step)}
                  retrying={retryingIndex === step.index}
                  onRetry={handleRetry}
                />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Overall batch status chip — color-coded for glanceable status.
 */
const StatusChip: React.FC<{ status: SiteBatch['status'] }> = ({ status }) => {
  const map: Record<SiteBatch['status'], { label: string; color: 'primary' | 'success' | 'warning' | 'default' }> = {
    pending: { label: '준비 중', color: 'primary' },
    running: { label: '진행 중', color: 'primary' },
    completed: { label: '완료', color: 'success' },
    cancelled: { label: '취소됨', color: 'default' },
  };
  const entry = map[status] ?? { label: status, color: 'default' as const };
  return <Chip label={entry.label} size="small" color={entry.color} />;
};

/**
 * Header summary: step progress bar + success/failed chips.
 */
const SummaryBar: React.FC<{ batch: SiteBatch }> = ({ batch }) => {
  const total = batch.total_steps || batch.steps.length || 1;
  const finished = batch.success_count + batch.failed_count;
  const pct = Math.min(100, Math.round((finished / total) * 100));
  const running = isBatchRunning(batch.status);

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          진행 {finished}/{total}
        </Typography>
        <Chip label={`성공 ${batch.success_count}`} size="small" color="success" variant="outlined" />
        <Chip label={`실패 ${batch.failed_count}`} size="small" color="error" variant="outlined" />
        {running && (
          <Typography variant="caption" color="primary.main">
            현재 단계: {batch.current_step}/{total}
          </Typography>
        )}
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
};

export default SiteBatchProgressModal;
